import { Chip, Stack, Typography, useTheme } from "@mui/material";
import { MoreHorizontal } from "lucide-react";
import { type FC, useState } from "react";
import { FutureItemActionDrawer } from "web_myplann/Goals/Components/FutureItemActionDrawer";
import { SolutionRow } from "web_myplann/Goals/Components/SolutionRow";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { isLoanFutureType } from "web_myplann/Goals/Helpers/futureContractForm";
import type { GoalFuturePortfolioResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ConfirmDrawer } from "web_myplann/Shared/Components/ConfirmDrawer";
import { joinTruthy } from "web_myplann/Shared/Utils/textFormatters";

type Props = {
	futurePortfolio: GoalFuturePortfolioResponseDTO;
	hasDivider: boolean;
	dueYear: number;
	onEdit: (futurePortfolio: GoalFuturePortfolioResponseDTO) => void;
	onDelete: (id: number) => void;
};

export const GoalDetailFutureItem: FC<Props> = ({
	futurePortfolio,
	hasDivider,
	dueYear,
	onEdit,
	onDelete,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const [actionOpen, setActionOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const closeAction = () => setActionOpen(false);

	const { currency } = futurePortfolio;
	const oneTime = futurePortfolio.oneTimeDeposit ?? 0;
	const monthly = futurePortfolio.monthlyAmount ?? 0;

	const monthlyLabel = t(Resources.Goals.Detail.Solution.monthlyLabel, {
		amount: formatAmount(monthly, currency),
	});

	const valueLabel = isLoanFutureType(futurePortfolio.type)
		? t(Resources.Goals.Detail.Solution.loanLabel, {
				amount: formatAmount(oneTime, currency),
			})
		: oneTime
			? t(Resources.Goals.Detail.Solution.lumpSumLabel, {
					amount: formatAmount(oneTime, currency),
				})
			: monthlyLabel;

	const subtitle = joinTruthy(
		[
			t(Resources.Goals.Detail.Solution.Pin.Future.Types[futurePortfolio.type]),
			oneTime > 0 && monthly > 0 && monthlyLabel,
		],
		" · ",
	);

	const expectedValue = futurePortfolio.expectedCoverage;

	return (
		<>
			<SolutionRow hasDivider={hasDivider} onClick={() => setActionOpen(true)}>
				<Stack flex={1} minWidth={0} gap={0.2}>
					<Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
						<Typography variant="bodyMedium" noWrap>
							{futurePortfolio.name}
						</Typography>
						<Chip
							variant="tag"
							label={t(Resources.Goals.Detail.Solution.futureBadge)}
						/>
					</Stack>
					<Typography variant="caption" color="text.secondary" noWrap>
						{subtitle}
					</Typography>
				</Stack>
				<Stack alignItems="flex-end" flexShrink={0} gap={0.4}>
					<Typography variant="bodyStrong" tabular>
						{valueLabel}
					</Typography>
					{expectedValue > 0 && (
						<Typography
							variant="caption"
							color={theme.palette.primary.main}
							tabular
						>
							{t(Resources.Goals.Detail.Solution.expectedInYear, {
								amount: formatAmount(expectedValue),
								year: dueYear,
							})}
						</Typography>
					)}
				</Stack>
				<MoreHorizontal size={16} color={theme.palette.grey[400]} />
			</SolutionRow>
			<FutureItemActionDrawer
				open={actionOpen}
				futurePortfolio={futurePortfolio}
				onClose={closeAction}
				onEdit={onEdit}
				onRequestDelete={() => setConfirmOpen(true)}
			/>
			<ConfirmDrawer
				open={confirmOpen}
				title={t(
					Resources.Goals.Detail.Solution.Pin.Future.DeleteConfirm.title,
				)}
				description={t(
					Resources.Goals.Detail.Solution.Pin.Future.DeleteConfirm.description,
					{ name: futurePortfolio.name },
				)}
				confirmLabel={t(
					Resources.Goals.Detail.Solution.Pin.Future.DeleteConfirm.confirm,
				)}
				cancelLabel={t(
					Resources.Goals.Detail.Solution.Pin.Future.DeleteConfirm.cancel,
				)}
				destructive
				onClose={() => setConfirmOpen(false)}
				onConfirm={() => {
					setConfirmOpen(false);
					onDelete(futurePortfolio.id);
				}}
			/>
		</>
	);
};
